import { createRng } from "./util/rng.js";

/**
 * The burning-city backdrop.
 *
 * There are no image assets and no backend, so the scenes are drawn: a firelit
 * horizon, layered skylines, smoke and embers. Each scene is a pure function of
 * its index, so a given scene looks the same everywhere, and the backdrop never
 * depends on the network.
 */

export const SCENE_INTERVAL_MS = 44_000;
const FADE_MS = 9_000;

export interface BackgroundOptions {
  reducedMotion?: boolean;
  intervalMs?: number;
}

export interface BackgroundHandle {
  dispose(): void;
  sceneIndex(): number;
}

type CanvasLayer = { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D };

function buildLayer(doc: Document, root: Element): CanvasLayer {
  const canvas = doc.createElement("canvas");
  canvas.className = "background-scene";
  canvas.setAttribute("aria-hidden", "true");
  root.append(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas unavailable for the backdrop");
  return { canvas, ctx };
}

function sizeLayer(layer: CanvasLayer): { width: number; height: number } {
  const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
  const width = Math.max(1, Math.round(layer.canvas.clientWidth || 1));
  const height = Math.max(1, Math.round(layer.canvas.clientHeight || 1));
  layer.canvas.width = Math.round(width * dpr);
  layer.canvas.height = Math.round(height * dpr);
  layer.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { width, height };
}

function drawSkyline(
  ctx: CanvasRenderingContext2D,
  rng: ReturnType<typeof createRng>,
  width: number,
  height: number,
  base: number,
  color: string,
  scale: number,
  glow: number,
): void {
  let x = -rng.range(20, 70);
  while (x < width + 40) {
    const w = rng.range(16, 62) * scale;
    const h = rng.range(0.04, 0.19) * height * scale;
    const top = base - h;

    ctx.fillStyle = color;
    ctx.fillRect(x, top, w, base - top + 2);
    if (glow > 0.8) {
      ctx.fillStyle = `rgba(255, 156, 88, ${0.09 * glow})`;
      ctx.fillRect(x, top, w, Math.max(1, scale));
    }

    if (rng.chance(0.3)) {
      const spike = rng.range(4, 18) * scale;
      const spikeX = x + w * rng.range(0.2, 0.7);
      ctx.fillRect(spikeX, top - spike, Math.max(1, scale), spike);
    }

    const columns = Math.floor(w / (7 * scale));
    const rows = Math.floor(h / (9 * scale));
    for (let c = 0; c < columns; c += 1) {
      for (let r = 0; r < rows; r += 1) {
        if (!rng.chance(0.07 * glow)) continue;
        ctx.fillStyle = `rgba(255, 168, 92, ${rng.range(0.12, 0.5) * glow})`;
        ctx.fillRect(
          x + c * 7 * scale + 2,
          top + r * 9 * scale + 3,
          Math.max(1, 2 * scale),
          Math.max(1, 3 * scale),
        );
        ctx.fillStyle = color;
      }
    }

    x += w + rng.range(3, 14) * scale;
  }
}

/** One firelit skyline. Same index, same picture: this is asserted by a test. */
export function drawScene(
  ctx: CanvasRenderingContext2D,
  sceneIndex: number,
  width: number,
  height: number,
): void {
  const rng = createRng(`backdrop:${sceneIndex}`);
  const horizon = height * rng.range(0.6, 0.7);
  const fires = Array.from({ length: rng.int(1, 4) }, () => ({
    x: width * rng.range(0.12, 0.88),
    strength: rng.range(0.5, 1),
    spread: rng.range(0.3, 0.62),
  }));

  ctx.clearRect(0, 0, width, height);

  const sky = ctx.createLinearGradient(0, 0, 0, horizon * 1.1);
  sky.addColorStop(0, "#06070a");
  sky.addColorStop(0.5, "#0d121a");
  sky.addColorStop(0.82, "#241613");
  sky.addColorStop(1, "#361b13");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, horizon + 2);

  for (const fire of fires) {
    const glow = ctx.createRadialGradient(fire.x, horizon, 0, fire.x, horizon, height * fire.spread);
    glow.addColorStop(0, `rgba(255, 150, 72, ${0.42 * fire.strength})`);
    glow.addColorStop(0.32, `rgba(212, 94, 46, ${0.17 * fire.strength})`);
    glow.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, horizon + 2);
  }

  const haze = ctx.createLinearGradient(0, horizon - height * 0.16, 0, horizon);
  haze.addColorStop(0, "rgba(120, 66, 44, 0)");
  haze.addColorStop(1, "rgba(132, 76, 48, 0.22)");
  ctx.fillStyle = haze;
  ctx.fillRect(0, horizon - height * 0.16, width, height * 0.16);

  drawSkyline(ctx, rng, width, height, horizon - height * 0.02, "rgba(10, 13, 19, 0.9)", 0.72, 1);
  drawSkyline(ctx, rng, width, height, horizon + height * 0.02, "rgba(7, 9, 13, 0.95)", 1, 0.9);
  drawSkyline(ctx, rng, width, height, horizon + height * 0.08, "rgba(4, 5, 8, 1)", 1.35, 0.55);

  for (const fire of fires) {
    const plumes = 26;
    for (let i = 0; i < plumes; i += 1) {
      const t = i / plumes;
      const radius = (8 + t * 58) * fire.strength;
      const x = fire.x + Math.sin(i * 0.7 + sceneIndex) * radius * 0.4 + t * width * 0.06;
      const y = horizon - t * height * 0.52;
      const smoke = ctx.createRadialGradient(x, y, 0, x, y, radius);
      smoke.addColorStop(0, `rgba(52, 38, 34, ${0.24 * (1 - t) * fire.strength})`);
      smoke.addColorStop(1, "rgba(30, 22, 20, 0)");
      ctx.fillStyle = smoke;
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }

    const embers = rng.int(20, 60);
    for (let i = 0; i < embers; i += 1) {
      const x = fire.x + rng.range(-90, 90) * fire.strength;
      const y = horizon - rng.range(0, height * 0.34);
      const radius = rng.range(0.6, 1.7);
      ctx.fillStyle = `rgba(255, ${rng.int(140, 205)}, ${rng.int(80, 140)}, ${rng.range(0.18, 0.7)})`;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const ground = ctx.createLinearGradient(0, horizon, 0, height);
  ground.addColorStop(0, "rgba(6, 6, 8, 0)");
  ground.addColorStop(1, "rgba(4, 4, 6, 0.86)");
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon - 4, width, height - horizon + 4);
}

export function createBackground(root: Element, options: BackgroundOptions = {}): BackgroundHandle {
  const doc = root.ownerDocument;
  const reducedMotion =
    options.reducedMotion ??
    (typeof globalThis.matchMedia === "function"
      ? globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false);
  const interval = options.intervalMs ?? SCENE_INTERVAL_MS;

  const layers = [buildLayer(doc, root), buildLayer(doc, root)];
  let front = 0;
  let index = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let resizeTimer: ReturnType<typeof setTimeout> | null = null;

  const paint = (layer: CanvasLayer, scene: number) => {
    const { width, height } = sizeLayer(layer);
    drawScene(layer.ctx, scene, width, height);
  };

  const show = (scene: number) => {
    front = 1 - front;
    const incoming = layers[front] as CanvasLayer;
    const outgoing = layers[1 - front] as CanvasLayer;
    paint(incoming, scene);
    incoming.canvas.style.opacity = "1";
    outgoing.canvas.style.opacity = "0";
    index = scene;
  };

  const first = layers[front] as CanvasLayer;
  paint(first, index);
  first.canvas.style.opacity = "1";
  // The first scene is simply there. Fading the backdrop in on every reload
  // would make the station look like it is still starting up for nine seconds.
  requestAnimationFrame(() => {
    for (const layer of layers) layer.canvas.style.transition = `opacity ${FADE_MS}ms linear`;
  });

  if (!reducedMotion) {
    timer = setInterval(() => {
      if (doc.visibilityState === "hidden") return;
      show(index + 1);
    }, interval);
  }

  const onResize = () => {
    if (resizeTimer !== null) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      paint(layers[front] as CanvasLayer, index);
      resizeTimer = null;
    }, 180);
  };
  doc.defaultView?.addEventListener("resize", onResize);

  return {
    sceneIndex: () => index,
    dispose() {
      if (timer !== null) clearInterval(timer);
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      timer = null;
      resizeTimer = null;
      doc.defaultView?.removeEventListener("resize", onResize);
      for (const layer of layers) layer.canvas.remove();
    },
  };
}
